/**
 * Modal para ver y subir fotos crowdsourced de una lámina específica.
 * Se abre desde el botón "📸" del BookSlot cuando una lámina está pegada.
 */
import { useEffect, useRef, useState } from "react";
import { Icon } from "./Icon";
import { Avatar } from "./components";
import { useToast } from "./toast";
import { useAuth } from "../contexts/AuthContext";
import {
  deleteStickerPhoto,
  fetchStickerPhotos,
  setPrimary,
  uploadStickerPhoto,
  type StickerPhoto,
} from "../api/stickerPhotos";
import { PhotoEditor } from "./PhotoEditor";

interface Props {
  stickerId: number;
  stickerNum: string;
  stickerName: string;
  /** Imagen del catálogo si existe (se muestra en el grid junto a las comunitarias). */
  catalogImageUrl?: string | null;
  onClose: () => void;
  /** Llamado cuando se sube/borra una foto, por si el caller quiere refrescar. */
  onChanged?: () => void;
}

export function StickerPhotoModal({ stickerId, stickerNum, stickerName, catalogImageUrl, onClose, onChanged }: Props) {
  const { user } = useAuth();
  const toast = useToast();
  const [photos, setPhotos] = useState<StickerPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editorImage, setEditorImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const reload = () => {
    setLoading(true);
    fetchStickerPhotos(stickerId)
      .then(setPhotos)
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stickerId]);

  const onFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast("Por favor selecciona una imagen");
      return;
    }
    // Cargar como dataURL para abrir el editor
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result;
      if (typeof result === "string") setEditorImage(result);
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleEditorConfirm = async (blob: Blob) => {
    setUploading(true);
    try {
      const file = new File([blob], `sticker-${stickerId}.jpg`, { type: "image/jpeg" });
      await uploadStickerPhoto(stickerId, file);
      const okMsg = isAdmin ? "Foto oficial actualizada para todos 📸" : "Foto subida · gracias por compartir 📸";
      toast(okMsg);
      setEditorImage(null);
      reload();
      onChanged?.();
    } catch (err) {
      toast(`Error: ${err instanceof Error ? err.message : err}`);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId: number) => {
    if (!confirm("¿Borrar esta foto?")) return;
    try {
      await deleteStickerPhoto(photoId);
      toast("Foto eliminada");
      reload();
      onChanged?.();
    } catch (e) {
      toast(`Error: ${e}`);
    }
  };

  const handleSetPrimary = async (photoId: number) => {
    if (!confirm("¿Marcar esta foto como oficial? Reemplaza la actual del catálogo.")) return;
    try {
      await setPrimary(photoId);
      toast("Foto marcada como oficial 🌟");
      reload();
      onChanged?.();
    } catch (e) {
      toast(`Error: ${e}`);
    }
  };

  if (editorImage) {
    return (
      <PhotoEditor
        imageSrc={editorImage}
        onCancel={() => setEditorImage(null)}
        onConfirm={handleEditorConfirm}
      />
    );
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "grid", placeItems: "center", zIndex: 999, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg)", borderRadius: 14, maxWidth: 640, width: "100%", maxHeight: "92vh", overflowY: "auto", padding: 22 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
          <div>
            <div className="tiny muted" style={{ marginBottom: 2 }}>📸 Fotos de la lámina</div>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 800, margin: 0 }}>
              {stickerNum} · {stickerName}
            </h2>
          </div>
          <div style={{ flex: 1 }} />
          <button className="icon-btn" onClick={onClose} aria-label="Cerrar"><Icon name="x" size={16} /></button>
        </div>

        <p style={{ color: "var(--fg-muted)", fontSize: 13, margin: "0 0 16px" }}>
          ¿Tienes esta lámina física? <b>Tómale una foto</b> y compártela con la comunidad.
          Las fotos quedan visibles para todos los coleccionistas.
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onFileChosen}
          style={{ display: "none" }}
        />

        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <button className="btn btn-primary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            📷 {uploading ? "Subiendo…" : "Subir foto de mi lámina"}
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 24, color: "var(--fg-muted)" }}>Cargando fotos…</div>
        ) : (
          <>
            {(photos.length > 0 || catalogImageUrl) && (
              <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 700, color: "var(--fg-muted)" }}>
                {photos.length} foto{photos.length === 1 ? "" : "s"} de la comunidad
                {catalogImageUrl ? " + 1 del catálogo" : ""}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
              {catalogImageUrl && (
                <div style={{ position: "relative", aspectRatio: "1/1", borderRadius: 10, overflow: "hidden", border: "1px solid var(--border)" }}>
                  <img
                    src={catalogImageUrl}
                    alt={stickerName}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                  <div style={{ position: "absolute", bottom: 4, left: 4, background: "rgba(0,0,0,0.7)", color: "white", fontSize: 9, padding: "2px 6px", borderRadius: 4 }}>
                    📚 Catálogo
                  </div>
                </div>
              )}
              {photos.map((p) => {
                const canDelete = user && (p.uploader?.id === user.id || isAdmin);
                const canSetPrimary = isAdmin && !p.is_primary;
                return (
                  <div key={p.id} style={{ position: "relative", aspectRatio: "1/1", borderRadius: 10, overflow: "hidden", border: p.is_primary ? "2px solid var(--accent-amber)" : "1px solid var(--border)" }}>
                    <img
                      src={p.public_url}
                      alt={`Foto de ${stickerName}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      loading="lazy"
                    />
                    {p.is_primary && (
                      <div style={{ position: "absolute", top: 4, left: 4, background: "var(--accent-amber)", color: "#4a2900", fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 6 }}>
                        🌟 OFICIAL
                      </div>
                    )}
                    {!p.is_primary && p.uploader && (
                      <div style={{ position: "absolute", top: 4, left: 4, display: "flex", alignItems: "center", gap: 4, background: "rgba(0,0,0,0.7)", color: "white", borderRadius: 999, padding: "2px 6px" }}>
                        <Avatar user={p.uploader} />
                        <span style={{ fontSize: 10, fontWeight: 600, paddingRight: 4 }}>{p.uploader.name.split(" ")[0]}</span>
                      </div>
                    )}
                    <div style={{ position: "absolute", top: 4, right: 4, display: "flex", gap: 4 }}>
                      {canSetPrimary && (
                        <button
                          onClick={() => handleSetPrimary(p.id)}
                          style={{ background: "rgba(245,158,11,0.95)", color: "#4a2900", border: 0, padding: "4px 6px", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 700 }}
                          title="Marcar como foto oficial"
                        >
                          🌟
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(p.id)}
                          style={{ background: "rgba(220,38,38,0.85)", color: "white", border: 0, padding: "4px 6px", borderRadius: 6, cursor: "pointer", fontSize: 11 }}
                          title="Borrar foto"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {photos.length === 0 && !catalogImageUrl && (
              <div style={{ textAlign: "center", padding: 32, color: "var(--fg-muted)", border: "2px dashed var(--border)", borderRadius: 12 }}>
                <Icon name="grid" size={28} />
                <div style={{ marginTop: 8, fontWeight: 600 }}>Aún no hay fotos de esta lámina</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>Sé el primero en compartir una</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
